import type { CSSProperties, ReactNode } from 'react'
import { showDate } from '../../../shared/tree'
import { StoryImg } from '@/components/storybook/PickedUp'
import { peopleIn, type Album, type AlbumPhoto } from '@/lib/albums'
import { fills, lookOn, MARGINS, type Look, type PlanPage } from '@/lib/print/looks'
import { photoPlates, placed, rowOf, type Box } from '@/lib/print/photoPage'
import { coverOf, OTHER_TIMES } from '@/lib/storybook'

// A printed photo book's pages drawn on screen, to scale: measures are in inches of the trimmed page and type in
// points, the same as the PDF (lib/print/storybook.ts) and from the same plan and layout, so the book turned in the
// builder is the book that's printed. Edge to edge photos run past the page here as they run into the bleed there.

export type Trim = { width: number; height: number }
// What lies round the plan's pages in a bound book: its cover, the endpapers and the back cover.
export type BookFace = PlanPage | { kind: 'cover' } | { kind: 'endpaper' } | { kind: 'back' }

const BLEED = 0.125
const SAFETY = 0.5
const LINE = 10.5 / 72
const pt = (n: number) => `calc(var(--in) * ${n / 72})`
const at = (b: Box, trim: Trim): CSSProperties => ({
  left: `${(b.x / trim.width) * 100}%`,
  top: `${(b.y / trim.height) * 100}%`,
  width: `${(b.width / trim.width) * 100}%`,
  height: `${(b.height / trim.height) * 100}%`,
})

export function BookFaceView({
  face,
  album,
  look: bookLook,
  trim,
  number,
}: {
  face: BookFace
  album: Album
  look: Look
  trim: Trim
  // The page's number in the printed interior, when it shows one.
  number?: number
}) {
  // A page given edges of its own is drawn with them.
  const look = lookOn(bookLook, face)
  const traditional = look.type === 'traditional'
  return (
    <div
      className="kp-page"
      data-type={look.type}
      style={
        {
          '--tw': trim.width,
          background: look.paper === 'natural' ? 'var(--sheet-natural)' : undefined,
        } as CSSProperties
      }
    >
      <div className="kp-paper">
        <Face face={face} album={album} look={look} trim={trim} traditional={traditional} />
        {number !== undefined && folios(face, look) && (
          <span className={number % 2 ? 'kp-folio is-right' : 'kp-folio'}>{number}</span>
        )}
      </div>
    </div>
  )
}

const folios = (face: BookFace, look: Look) =>
  face.kind === 'words' ||
  face.kind === 'people' ||
  face.kind === 'notes' ||
  (face.kind === 'photos' && !fills(look.edges))

function Face({
  face,
  album,
  look,
  trim,
  traditional,
}: {
  face: BookFace
  album: Album
  look: Look
  trim: Trim
  traditional: boolean
}) {
  switch (face.kind) {
    case 'cover':
    case 'title':
      return <TitlePage album={album} look={look} trim={trim} traditional={traditional} />
    case 'photos':
      return <PhotosPage page={face} look={look} trim={trim} traditional={traditional} />
    case 'words':
      return (
        <Column look={look} trim={trim} center>
          <p className="kp-eyebrow">A few words</p>
          <p className="kp-title kp-words" style={{ fontSize: pt(traditional ? 19 : 17) }}>
            {album.description.trim()}
          </p>
          {album.by && <p style={{ fontSize: pt(9) }}>{album.by}</p>}
        </Column>
      )
    case 'people':
      return (
        <Column look={look} trim={trim}>
          <p className="kp-eyebrow">The people in this book</p>
          <ul className="kp-people">
            {peopleIn(album.photos).map((person) => (
              <li key={person.id}>
                <span className="kp-title" style={{ fontSize: pt(traditional ? 12 : 10.5) }}>
                  {person.name}
                </span>
                <span className="kp-muted" style={{ fontSize: pt(8) }}>
                  {person.living === false ? 'In memory · ' : ''}
                  {person.photos === 1 ? '1 photo' : `${person.photos} photos`}
                </span>
              </li>
            ))}
          </ul>
        </Column>
      )
    case 'notes':
      return (
        <div className="kp-notes">
          <p style={{ fontFamily: 'var(--font-book)', fontSize: pt(22) }}>Notes</p>
        </div>
      )
    case 'closing':
      return (
        <div className="kp-closing">
          <span className="kp-rule" />
          <p className="kp-title" style={{ fontSize: pt(12) }}>
            {album.title}
          </p>
          <p className="kp-muted" style={{ fontSize: pt(8) }}>
            {album.by ? `A Storybook version, from the album of ${album.by}.` : 'A Storybook version of the album.'}
          </p>
        </div>
      )
    case 'back':
      return (
        <div className="kp-back">
          <p className="kp-eyebrow">Storybook version</p>
          <p className="kp-title" style={{ fontSize: pt(16) }}>
            {album.title}
          </p>
          {album.by && (
            <p className="kp-muted" style={{ fontSize: pt(8) }}>
              From the album of {album.by}
            </p>
          )}
        </div>
      )
    case 'blank':
    case 'endpaper':
      return null
  }
}

// Words set inside the margins of a page of words.
function Column({ look, trim, center, children }: { look: Look; trim: Trim; center?: boolean; children: ReactNode }) {
  const m = look.edges === 'wide' ? MARGINS.wide : MARGINS.border
  return (
    <div
      className="kp-column"
      data-center={center || undefined}
      style={at({ x: m.side, y: m.top, width: trim.width - 2 * m.side, height: trim.height - m.top - m.bottom }, trim)}
    >
      {children}
    </div>
  )
}

// The title page and the front cover, in the look's cover style, as print/storybook.ts titlePage sets them.
function TitlePage({ album, look, trim, traditional }: { album: Album; look: Look; trim: Trim; traditional: boolean }) {
  const cover = coverOf(album)
  let y = trim.height * 0.4
  let picture: ReactNode
  if (cover && look.cover === 'photo') {
    const h = trim.height * 0.64
    picture = (
      <Photo
        photo={cover}
        box={{ x: -BLEED, y: -BLEED, width: trim.width + 2 * BLEED, height: h + BLEED }}
        trim={trim}
        eager
      />
    )
    y = h + 0.55
  } else if (cover && look.cover === 'window') {
    const side = Math.min(trim.width * 0.56, trim.height * 0.46)
    const box = { x: (trim.width - side) / 2, y: trim.height * 0.13, width: side, height: side }
    picture = <Photo photo={cover} box={box} trim={trim} whole eager />
    y = box.y + side + 0.6
  } else {
    picture = (
      <span
        className="kp-rule"
        style={{ position: 'absolute', left: '50%', top: `${((y - 0.45) / trim.height) * 100}%` }}
      />
    )
  }
  return (
    <>
      {picture}
      <div className="kp-titles" style={{ top: `calc(${(y / trim.height) * 100}% - ${pt(7)})` }}>
        <p className="kp-eyebrow">
          {album.collection ? `${album.collection.title} · Storybook version` : 'Storybook version'}
        </p>
        <p className="kp-title kp-big" style={{ fontSize: pt(traditional ? 34 : 30) }}>
          {album.title}
        </p>
        {album.by && (
          <p className="kp-muted" style={{ fontSize: pt(9.5) }}>
            From the album of {album.by}
          </p>
        )}
      </div>
    </>
  )
}

function Photo({
  photo,
  box,
  trim,
  whole,
  eager,
}: {
  photo: AlbumPhoto
  box: Box
  trim: Trim
  whole?: boolean
  eager?: boolean
}) {
  return (
    <div className={whole ? 'kp-photo is-whole' : 'kp-photo'} style={at(box, trim)}>
      <StoryImg photo={photo} sizes="(min-width: 64rem) 30rem, 90vw" eager={eager} />
    </div>
  )
}

// The room a photo's words take under it, in inches: its caption on up to two lines (judged by its length at the
// caption's size, as the PDF wraps it) and the day it was taken.
function wordsRoom(photo: AlbumPhoto, width: number, traditional: boolean) {
  const em = (traditional ? 9.5 : 8) / 72
  const lines = photo.caption.trim() ? Math.min(2, Math.ceil((photo.caption.trim().length * em * 0.5) / width)) : 0
  return (lines + (photo.takenOn ? 1 : 0)) * LINE
}

function PhotosPage({
  page,
  look,
  trim,
  traditional,
}: {
  page: Extract<PlanPage, { kind: 'photos' }>
  look: Look
  trim: Trim
  traditional: boolean
}) {
  const fill = fills(look.edges)
  const m = MARGINS[look.edges]
  const boxes = photoPlates({
    count: page.photos.length,
    trim,
    margins: m,
    fill,
    bleed: look.edges === 'bleed' ? BLEED : 0,
    opens: page.opens,
    gutter: 0,
    dx: 0,
  })
  const year = (page.year ?? OTHER_TIMES).toUpperCase()
  if (fill)
    return (
      <>
        {page.photos.map((photo, i) => (
          <div key={photo.id}>
            <Photo photo={photo} box={boxes[i]!} trim={trim} />
            {look.captions && (photo.caption.trim() || photo.takenOn) && (
              <Label box={boxes[i]!} trim={trim} corner="bottom" paper={look.paper}>
                {photo.caption.trim() && <span className="kp-caption">{photo.caption.trim()}</span>}
                {photo.takenOn && <span className="kp-muted">{showDate(photo.takenOn)}</span>}
              </Label>
            )}
          </div>
        ))}
        {page.opens && (
          <Label
            box={{ x: 0, y: 0, width: trim.width, height: trim.height }}
            trim={trim}
            corner="top"
            paper={look.paper}
          >
            <span className="kp-eyebrow">{year}</span>
          </Label>
        )}
      </>
    )
  const rooms = page.photos.map((photo, i) => (look.captions ? wordsRoom(photo, boxes[i]!.width, traditional) : 0))
  const most = Math.max(...rooms)
  const room = most ? most + 0.09 : 0
  return (
    <>
      {page.opens && (
        <p className="kp-eyebrow kp-year" style={{ top: `calc(${(m.top / trim.height) * 100}% + ${pt(1)})` }}>
          {year}
        </p>
      )}
      {page.photos.map((photo, i) => {
        const plate = boxes[i]!
        const box = placed(plate, photo.width / photo.height, room, rowOf(page.photos.length, i))
        return (
          <div key={photo.id}>
            <Photo photo={photo} box={box} trim={trim} whole />
            {room > 0 && (
              <div
                className="kp-words-under"
                style={{
                  ...at({ x: plate.x, y: box.y + box.height + 0.09, width: plate.width, height: room }, trim),
                }}
              >
                {photo.caption.trim() && (
                  <span className="kp-caption" style={{ fontSize: pt(traditional ? 9.5 : 8) }}>
                    {photo.caption.trim()}
                  </span>
                )}
                {photo.takenOn && (
                  <span className="kp-muted" style={{ fontSize: pt(traditional ? 8.5 : 7.5) }}>
                    {showDate(photo.takenOn)}
                  </span>
                )}
              </div>
            )}
          </div>
        )
      })}
    </>
  )
}

// A small paper label in a corner of a box, inside the safe area, as the PDF sets captions on filled photos.
function Label({
  box,
  trim,
  corner,
  paper,
  children,
}: {
  box: Box
  trim: Trim
  corner: 'top' | 'bottom'
  paper: Look['paper']
  children: ReactNode
}) {
  const left = Math.max(box.x + 0.18, SAFETY)
  const style: CSSProperties = {
    left: `${(left / trim.width) * 100}%`,
    maxWidth: `${((trim.width - SAFETY - left) / trim.width) * 100}%`,
    background: paper === 'natural' ? 'var(--sheet-natural)' : undefined,
  }
  if (corner === 'top') style.top = `${(Math.max(box.y + 0.18, SAFETY) / trim.height) * 100}%`
  else
    style.bottom = `${((trim.height - Math.min(box.y + box.height - 0.18, trim.height - SAFETY)) / trim.height) * 100}%`
  return (
    <span className="kp-label" style={style}>
      {children}
    </span>
  )
}
